/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RECAPTCHA_ACTIONS } from '../constants/recaptcha';
import { TWO_FA_LOGIN_METHOD } from '../constants/twoFa';
import type { AccessTokenResponse } from '../managers/sso';
import {
  accountKeys,
  apiKeyQueryOptions,
  handleLoginError,
  handleSuccessfulLogin,
  useRecaptchaManager,
  useSsoApiService,
} from './account';

/** Signed in outright, or still owes an emailed OTP. */
export type CredentialsLoginResult =
  | { status: 'twoFactorRequired'; twoFAToken: string }
  | { status: 'loggedIn'; data: AccessTokenResponse };

/** Email + password sign-in under 2FA; picked by the flag at the caller. */
export const useLoginByCredentials2Fa = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();

  return useMutation({
    mutationFn: async ({
      username,
      password,
      restoreAccount,
    }: {
      username: string;
      password: string;
      /** Keep-account retry: cancels the pending deletion; 2FA still applies. */
      restoreAccount?: boolean;
    }): Promise<CredentialsLoginResult> => {
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api),
      );

      if (!clientKey) throw new Error('Missing API keys');

      const clientId = clientKey.id || '';
      const apiKey = clientKey.apiKey || '';

      // `supportsDirectLogin`: accounts needing no 2FA return the session here.
      const firstAuth = await api.loginFirstAuthentication(
        {
          username,
          password,
          status: '',
          supportsDirectLogin: true,
          ...(restoreAccount ? { restoreAccount } : {}),
        },
        clientId,
        apiKey,
        await recaptchaManager.execute(
          RECAPTCHA_ACTIONS.LOGIN_2FA_BY_CREDENTIALS,
        ),
      );

      // Already signed in — no OTP step.
      if (firstAuth?.accessToken) {
        return { status: 'loggedIn', data: firstAuth } as const;
      }

      if (!firstAuth?.twoFAToken) {
        throw new Error('First authentication returned no two-factor token');
      }

      if (!firstAuth.available2FAMethods?.includes(TWO_FA_LOGIN_METHOD)) {
        throw new Error(
          `Account has no ${TWO_FA_LOGIN_METHOD} two-factor method available`,
        );
      }

      // Send the OTP to the user's email.
      const otpSent = await api.sendTwoFaOtp(
        { method: TWO_FA_LOGIN_METHOD },
        clientId,
        apiKey,
        await recaptchaManager.execute(
          RECAPTCHA_ACTIONS.LOGIN_2FA_METHOD(TWO_FA_LOGIN_METHOD),
        ),
        firstAuth.twoFAToken,
      );

      if (!otpSent?.twoFAToken) {
        throw new Error('Sending the two-factor code returned no token');
      }

      return {
        status: 'twoFactorRequired',
        twoFAToken: otpSent.twoFAToken,
      } as const;
    },
    // Only direct login carries a session; the 2FA path has none until verified.
    onSuccess: async (result) => {
      if (result.status === 'loggedIn') {
        await handleSuccessfulLogin(queryClient, result.data);
        return;
      }
      // Stored, not routed: a URL param would leak the token to history and logs.
      await AsyncStorage.setItem(
        accountKeys.twoFaLoginToken[0],
        result.twoFAToken,
      );
    },
    onError: async (error) => {
      await AsyncStorage.removeItem(accountKeys.twoFaLoginToken[0]);
      await handleLoginError(queryClient, error, 'Credentials');
    },
  });
};
